import { KybDecision, KybDecisionOutcome } from "./Decision";
import { RiskLevel } from "./RiskScore";

export interface DecisionResolutionInputs {
  riskLevel: RiskLevel;
  hasConfirmedTruePositiveHit: boolean;
  hasUnresolvedScreening: boolean;
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function resolveOutcome(inputs: DecisionResolutionInputs): KybDecisionOutcome {
  if (inputs.hasConfirmedTruePositiveHit) {
    return "REJECTED";
  }
  if (inputs.hasUnresolvedScreening) {
    return "REGULATORY_HOLD";
  }
  switch (inputs.riskLevel) {
    case "CRITICAL":
    case "HIGH":
      return "APPROVED_WITH_RESTRICTIONS";
    case "STANDARD":
    case "LOW":
      return "APPROVED_COMPLETE";
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function resolveJustification(
  outcome: KybDecisionOutcome,
  inputs: DecisionResolutionInputs,
): string {
  switch (outcome) {
    case "REJECTED":
      return "Hit sanctions confirmé — dossier rejeté conformément à la politique de tolérance zéro.";
    case "REGULATORY_HOLD":
      return "Screening non résolu — mise en attente réglementaire dans l'attente de qualification par la conformité.";
    case "APPROVED_COMPLETE":
    case "APPROVED_WITH_RESTRICTIONS":
      return `Décision automatisée (mock) sur la base du score de risque ${inputs.riskLevel}.`;
  }
}

/**
 * Deterministic stand-in for the external 4-eyes compliance decision
 * (US-KYB-04-03/04-04). The onboarding UI never lets the operator pick an
 * outcome — instruction (P3) and validation (P4) are separate actors with
 * server-enforced SoD — this mock only projects the resolved result, no
 * endpoint exists yet, see #25465.
 */
export function generateMock4EyesDecision(
  businessKey: string,
  inputs: DecisionResolutionInputs,
  resolvedAt: string,
): KybDecision {
  const outcome = resolveOutcome(inputs);
  return {
    businessKey,
    outcome,
    validatorName: "Comité conformité (4-yeux)",
    justification: resolveJustification(outcome, inputs),
    resolvedAt,
  };
}
